// cohorts/manage hooks
// TanStack Query and client-state hooks only. Pure API functions belong in services/.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@core/errors";
import { toast, toastApiError } from "@core/toast";
import { cohortKeys } from "../../_shared/keys";
import type { Cohort } from "../../_shared/types";
import {
  appointAdministrator,
  archiveCohort,
  restoreCohort,
  updateCohort,
} from "../services";
import { classifyManageFailure } from "../services/failure";
import type { CohortChanges } from "../types";

/** The answer is the cohort as it now is: put it in the detail cache, reload the lists. */
function useStoreCohort() {
  const queryClient = useQueryClient();
  return (cohort: Cohort) => {
    queryClient.setQueryData(cohortKeys.detail(cohort.id), cohort);
    void queryClient.invalidateQueries({ queryKey: cohortKeys.list() });
  };
}

/**
 * What a failed change says, as a toast. A stale revision or an archived cohort reloads the cohort
 * so the page shows what is true now.
 */
export function useManageFailureHandler(cohortId: string) {
  const queryClient = useQueryClient();

  return (error: unknown) => {
    const failure = classifyManageFailure(error);
    if (failure.kind === "revision_conflict" || failure.kind === "archived") {
      void queryClient.invalidateQueries({
        queryKey: cohortKeys.detail(cohortId),
      });
      toast.error(
        { key: `cohorts:settings.errors.${failure.kind}.title` },
        { key: `cohorts:settings.errors.${failure.kind}.description` }
      );
      return;
    }
    if (
      failure.kind === "name_invalid" ||
      failure.kind === "end_before_start" ||
      failure.kind === "administrator_unavailable"
    ) {
      // These sit on their fields, which the form already shows.
      return;
    }
    if (failure.kind === "gone") {
      void queryClient.invalidateQueries({ queryKey: cohortKeys.all });
      toast.error(
        { key: "cohorts:settings.errors.gone.title" },
        { key: "cohorts:settings.errors.gone.description" }
      );
      return;
    }
    if (isApiError(error)) {
      toastApiError(error);
      return;
    }
    toast.error(
      { key: "cohorts:settings.errors.unknown.title" },
      { key: "cohorts:settings.errors.unknown.description" }
    );
  };
}

/**
 * Saves the settings: the changed fields first, then the responsible Administrator (a separate
 * call that needs the revision the first one produced). Each call gets its own idempotency key,
 * drawn per save: the revision check refuses a second, stale save instead of applying it twice.
 */
export function useSaveSettings(cohort: Cohort) {
  const store = useStoreCohort();
  const onFailure = useManageFailureHandler(cohort.id);

  return useMutation({
    mutationFn: async (input: {
      changes: CohortChanges;
      administratorId: string | null;
    }) => {
      let current = cohort;
      if (Object.keys(input.changes).length > 0) {
        current = await updateCohort(
          cohort.id,
          input.changes,
          current.revision,
          crypto.randomUUID()
        );
      }
      if (input.administratorId) {
        current = await appointAdministrator(
          cohort.id,
          input.administratorId,
          current.revision,
          crypto.randomUUID()
        );
      }
      return current;
    },
    onSuccess: (saved) => {
      store(saved);
      toast.success(
        { key: "cohorts:settings.saved.title" },
        { key: "cohorts:settings.saved.description" }
      );
    },
    onError: onFailure,
  });
}

/** Archives the cohort (President). The caller leaves the page when it succeeds. */
export function useArchiveCohort(
  cohort: Pick<Cohort, "id" | "name" | "revision">
) {
  const store = useStoreCohort();
  const onFailure = useManageFailureHandler(cohort.id);

  return useMutation({
    mutationFn: () =>
      archiveCohort(cohort.id, cohort.revision, crypto.randomUUID()),
    onSuccess: (archived) => {
      store(archived);
      toast.success(
        { key: "cohorts:archive.archived.title", vars: { name: cohort.name } },
        { key: "cohorts:archive.archived.description" }
      );
    },
    onError: onFailure,
  });
}

/** Restores an archived cohort (President), from the list or the settings. */
export function useRestoreCohort() {
  const store = useStoreCohort();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cohort: Pick<Cohort, "id" | "name" | "revision">) =>
      restoreCohort(cohort.id, cohort.revision, crypto.randomUUID()),
    onSuccess: (restored) => {
      store(restored);
      toast.success(
        {
          key: "cohorts:archive.restored.title",
          vars: { name: restored.name },
        },
        { key: "cohorts:archive.restored.description" }
      );
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: cohortKeys.all });
      if (isApiError(error)) {
        toastApiError(error);
        return;
      }
      toast.error(
        { key: "cohorts:settings.errors.unknown.title" },
        { key: "cohorts:settings.errors.unknown.description" }
      );
    },
  });
}
